import * as React from "react";
import { ActionButton, Button, Card } from "@jhool-io/fe-components";
import { formatDate } from "../../../../../utils/helpers";
import CalendarIcon from "../../../../../icons/Calendar";
import UserIcon from "../../../../../icons/User";
import EditIcon from "../../../../../icons/Edit";
import DeleteIcon from "../../../../../icons/Delete";
import {
    ICollectionsNote,
    ICollectionsNoteDocument,
} from "../../../types/clients.types";
import { getCollectionsNoteUserName } from "../../../helpers/clients.helpers";
import CollectionsNoteActivity from "../CollectionsNoteActivity/CollectionsNoteActivity";

interface CollectionsNoteDetailsProps {
    collectionsNote: ICollectionsNote;
    canModify: boolean;
    onEdit(): void;
    onDelete(): void;
    onViewDocument(document: ICollectionsNoteDocument): void;
}

export default function CollectionsNoteDetails({
    collectionsNote,
    canModify,
    onEdit,
    onDelete,
    onViewDocument,
}: CollectionsNoteDetailsProps) {
    const [isActivityInView, setIsActivityInView] = React.useState(false);

    return (
        <>
            <div className="flex justify-between items-start gap-x-[--spacing-items] [border-bottom:1px_solid_var(--color-stroke)] pb-[32px]">
                <div className="flex-1 grid gap-y-8 gap-x-[--spacing-items] grid-cols-[repeat(auto-fit,minmax(160px,1fr))]">
                    <div className="flex flex-col">
                        <UserIcon className="size-5" />
                        <div className="flex flex-col gap-1 mt-3">
                            <span className="text-caption font-medium text-textSec uppercase">
                                Created by
                            </span>
                            <span className="text-base capitalize">
                                {getCollectionsNoteUserName(
                                    collectionsNote.author
                                )}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <CalendarIcon className="size-5" />
                        <div className="flex flex-col gap-1 mt-3">
                            <span className="text-caption font-medium text-textSec uppercase">
                                Date created
                            </span>
                            <span className="text-base">
                                {formatDate(collectionsNote.date_created)}
                            </span>
                        </div>
                    </div>
                    {collectionsNote.updated_by ? (
                        <div className="flex flex-col">
                            <CalendarIcon className="size-5" />
                            <div className="flex flex-col gap-1 mt-3">
                                <span className="text-caption font-medium text-textSec uppercase">
                                    Last updated
                                </span>
                                <span className="text-base capitalize">
                                    {`${formatDate(
                                        collectionsNote.date_updated
                                    )} by ${getCollectionsNoteUserName(
                                        collectionsNote.updated_by
                                    )}`}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </div>
                {canModify ? (
                    <div className="flex items-center shrink-0">
                        <ActionButton
                            variant="can-hover"
                            size="auto"
                            className="h-7"
                            actionTitle="Edit note"
                            onClick={onEdit}
                        >
                            <EditIcon />
                        </ActionButton>
                        <ActionButton
                            variant="can-hover"
                            size="auto"
                            className="h-7"
                            actionTitle="Delete note"
                            onClick={onDelete}
                        >
                            <DeleteIcon />
                        </ActionButton>
                    </div>
                ) : null}
            </div>

            <div className="py-[32px] [border-bottom:1px_solid_var(--color-stroke)]">
                <h3 className="text-caption font-medium text-textSec uppercase mb-2">
                    Description
                </h3>
                <p className="text-base whitespace-pre-wrap">
                    {collectionsNote.description}
                </p>
            </div>

            <div className="py-[32px] [border-bottom:1px_solid_var(--color-stroke)]">
                <h3 className="text-caption font-medium text-textSec uppercase mb-2">
                    Attachments
                </h3>
                {collectionsNote.documents.length > 0 ? (
                    <div className="flex flex-col gap-y-[--spacing-items]">
                        {collectionsNote.documents.map((doc) => (
                            <Card
                                key={doc.collections_note_document_id}
                                classNames="flex gap-x-1 py-2 justify-between items-center"
                            >
                                <ActionButton
                                    variant="can-hover"
                                    size="auto"
                                    className="h-7 !text-text text-caption underline"
                                    actionTitle="View"
                                    onClick={() => onViewDocument(doc)}
                                >
                                    {doc.document_name || "Attachment"}
                                </ActionButton>
                                <span className="text-caption text-textSec capitalize">
                                    {`${getCollectionsNoteUserName(
                                        doc.uploaded_by
                                    )}, ${formatDate(doc.created_at)}`}
                                </span>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <p className="text-base text-textSec">No attachments</p>
                )}
            </div>

            <div className="pt-[32px]">
                <div className="flex items-center justify-between gap-x-[--spacing-items]">
                    <h3 className="text-caption font-medium text-textSec uppercase">
                        Activity
                    </h3>
                    <Button
                        variant="secondary"
                        onClick={() => setIsActivityInView(!isActivityInView)}
                    >
                        {isActivityInView ? "Hide activity" : "View activity"}
                    </Button>
                </div>
                {isActivityInView ? (
                    <div className="mt-[--spacing-items]">
                        <CollectionsNoteActivity
                            clientId={collectionsNote.client_id}
                            collectionsNoteId={
                                collectionsNote.collections_note_id
                            }
                        />
                    </div>
                ) : null}
            </div>
        </>
    );
}
